import { Calculator, Hash, IndianRupee, Percent, Receipt, TrendingUp } from 'lucide-react';
import React, { useCallback, useEffect, useMemo } from 'react';
import { Control, Controller, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { AmountCalculator } from '../../services/amountCalculator';
import { useEnterpriseValidation } from '../../utils/enterpriseValidation';
import { cn } from '../../utils/tw';
import AmountInput from '../../ui/AmountInput';

interface UnifiedAmountModuleProps {
  register: UseFormRegister<any>;
  watch: UseFormWatch<any>;
  setValue: UseFormSetValue<any>;
  control: Control<any>;
  errors: any;
  disabled?: boolean;
  unitLabel?: string;
  className?: string;
  showQuantityMode?: boolean;
}

export const UnifiedAmountModule: React.FC<UnifiedAmountModuleProps> = ({
  register,
  watch,
  setValue,
  control,
  errors,
  disabled = false,
  unitLabel = "Units",
  className = "",
  showQuantityMode = true
}) => {
  // Initialize enterprise validation hooks
  const {
    validateField,
    sanitizeInput,
    isInputSecure,
    formatAmount,
    getValidationMessage
  } = useEnterpriseValidation();

  // Watch form values
  const mode = watch("mode") || "total";
  const quantity = watch("quantity") || "";
  const rate = watch("rate") || "";
  const gross = watch("gross") || "";
  const enableGst = watch("enableGst") ?? true;
  const gstAmount = watch("gstAmount") || "";

  // Calculate derived values using AmountCalculator
  const calculationResult = useMemo(() => {
    return AmountCalculator.computeTotals({
      mode,
      quantity,
      rate,
      gross,
      gstAmount,
      enableGst,
    });
  }, [mode, quantity, rate, gross, gstAmount, enableGst]);

  // Clear fields when mode changes
  useEffect(() => {
    if (mode === "total") {
      setValue("quantity", "", { shouldValidate: false });
      setValue("rate", "", { shouldValidate: false });
    } else if (mode === "unit") {
      setValue("gross", "", { shouldValidate: false });
    }
  }, [mode, setValue]);

  // Update form values when calculations change
  useEffect(() => {
    if (calculationResult.gross !== parseFloat(gross || "0")) {
      setValue("gross", calculationResult.gross.toString(), { shouldValidate: false });
    }
    if (calculationResult.gstPercent !== parseFloat(watch("gstPercent") || "0")) {
      setValue("gstPercent", calculationResult.gstPercent.toString(), { shouldValidate: false });
    }
    if (calculationResult.total !== parseFloat(watch("amount") || "0")) {
      setValue("amount", calculationResult.total.toString(), { shouldValidate: false });
    }
  }, [calculationResult, setValue, watch, gross]);

  // Memoized input handler with real-time sanitization
  const handleSecureInput = useCallback((field: string, value: string, inputType: 'amount' | 'quantity' | 'gst' = 'amount') => {
    const sanitized = sanitizeInput(value, inputType);

    // Update the field with sanitized value
    setValue(field, sanitized, { shouldValidate: true });

    return sanitized;
  }, [setValue, sanitizeInput]);

  return (
    <div className={cn("space-y-4", className)}>
      {/* Mode Selection with Simplified Design */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <Calculator className="h-4 w-4 text-gray-600" />
          <h4 className="text-sm font-medium text-gray-900">Calculation Method</h4>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className={cn(
            "flex items-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors",
            mode === "total"
              ? "border-info-500 bg-info-50"
              : "border-gray-200 bg-gray-50 hover:border-gray-300"
          )}>
            <input
              {...register("mode")}
              type="radio"
              value="total"
              disabled={disabled}
              className="h-4 w-4 text-info-600 focus:ring-info-500 border-gray-300"
            />
            <div>
              <div className="text-sm font-medium text-gray-900">Direct Amount</div>
              <div className="text-xs text-gray-500">Enter total amount</div>
            </div>
          </label>

          <label className={cn(
            "flex items-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors",
            mode === "unit"
              ? "border-info-500 bg-info-50"
              : "border-gray-200 bg-gray-50 hover:border-gray-300",
            !showQuantityMode && "opacity-50 cursor-not-allowed"
          )}>
            <input
              {...register("mode")}
              type="radio"
              value="unit"
              disabled={disabled || !showQuantityMode}
              className="h-4 w-4 text-info-600 focus:ring-info-500 border-gray-300"
            />
            <div>
              <div className="text-sm font-medium text-gray-900">Qty × Rate</div>
              <div className="text-xs text-gray-500">Calculate amount</div>
            </div>
          </label>
        </div>
      </div>

      {/* Input Fields with Clean Styling */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        {mode === "total" ? (
          /* Total Amount Mode */
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="h-4 w-4 text-gray-600" />
              <h4 className="text-sm font-medium text-gray-900">Amount Entry</h4>
            </div>

            <div>
              <Controller
                name="gross"
                control={control}
                rules={{
                  required: "Gross amount is required",
                  validate: (value) => {
                    if (!value || value.toString().trim() === '') return 'Gross amount is required';
                    const validation = validateField(value.toString(), 'amount');
                    return validation.isValid || validation.errors[0] || 'Invalid amount';
                  }
                }}
                render={({ field }) => (
                  <AmountInput
                    label="Gross Amount (Before Tax)"
                    required
                    value={field.value || 0}
                    onChange={(numValue) => {
                      field.onChange(numValue.toString());
                    }}
                    disabled={disabled}
                    error={errors.gross?.message}
                    helperText="Enter the amount before adding tax"
                    size="md"
                    fullWidth
                  />
                )}
              />
            </div>
          </div>
        ) : (
          /* Quantity × Rate Mode */
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-3">
              <Hash className="h-4 w-4 text-gray-600" />
              <h4 className="text-sm font-medium text-gray-900">Quantity-Based Calculation</h4>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Quantity Field */}
              <div>
                <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 mb-2">
                  Quantity <span className="text-error-500">*</span>
                </label>
                <div className="relative">
                  <input
                    {...register("quantity", {
                      required: "Quantity is required",
                      min: { value: 0.01, message: "Quantity must be greater than 0" }
                    })}
                    id="quantity"
                    type="number"
                    step="0.01"
                    min="0"
                    disabled={disabled}
                    placeholder="0.00"
                    className={cn(
                      'w-full px-3 py-3 pr-20 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-info-500 focus:border-info-500 transition-colors',
                      errors.quantity && 'border-error-500 focus:ring-red-500 focus:border-error-500',
                      disabled && 'opacity-50 cursor-not-allowed'
                    )}
                  />
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-500">
                    {unitLabel}
                  </div>
                </div>
                {errors.quantity && (
                  <p className="text-error-500 text-xs mt-1">{errors.quantity.message}</p>
                )}
              </div>

              {/* Rate Field */}
              <div>
                <Controller
                  name="rate"
                  control={control}
                  rules={{
                    required: "Rate is required",
                    min: { value: 0.01, message: "Rate must be greater than 0" }
                  }}
                  render={({ field }) => (
                    <AmountInput
                      label={`Rate per ${unitLabel}`}
                      required
                      value={field.value || 0}
                      onChange={(numValue) => {
                        field.onChange(numValue.toString());
                      }}
                      disabled={disabled}
                      error={errors.rate?.message}
                      helperText={`Price per individual ${unitLabel.toLowerCase()}`}
                      size="md"
                      fullWidth
                      min={0.01}
                    />
                  )}
                />
              </div>
            </div>

            {/* Auto-calculated Gross Amount Display */}
            <div className="bg-info-50 border border-info-200 rounded-lg p-3 mt-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-info-800 flex items-center gap-2">
                  <Calculator className="h-4 w-4" />
                  Calculated Amount:
                </span>
                <span className="text-lg font-semibold text-info-900">
                  ₹{gross || "0.00"}
                </span>
              </div>
              <div className="text-xs text-info-700">
                {quantity || "0"} {unitLabel} × ₹{rate || "0.00"} = ₹{gross || "0.00"}
              </div>
              <input {...register("gross")} type="hidden" value={gross} />
            </div>
          </div>
        )}
      </div>

      {/* GST Section with Clean Design */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <Receipt className="h-4 w-4 text-gray-600" />
          <h4 className="text-sm font-medium text-gray-900">Tax Configuration</h4>
        </div>

        <div className="flex items-center justify-between mb-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              {...register("enableGst")}
              type="checkbox"
              disabled={disabled}
              className="h-4 w-4 text-info-600 focus:ring-info-500 border-gray-300 rounded"
            />
            <span className="text-sm font-medium text-gray-700">Include GST/Tax</span>
          </label>
          <Percent className="h-4 w-4 text-gray-400" />
        </div>

        {enableGst && (
          <div>
            <Controller
              name="gstAmount"
              control={control}
              rules={{
                min: { value: 0, message: "GST amount cannot be negative" }
              }}
              render={({ field }) => (
                <AmountInput
                  label="GST/Tax Amount"
                  required
                  value={field.value || 0}
                  onChange={(numValue) => {
                    field.onChange(numValue.toString());
                  }}
                  disabled={disabled}
                  error={errors.gstAmount?.message}
                  helperText="Enter the GST/tax amount from your invoice (default: 0)"
                  size="md"
                  fullWidth
                  min={0}
                />
              )}
            />
          </div>
        )}

        {/* GST Percentage Display (Read-only) */}
        {enableGst && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mt-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">GST Percentage:</span>
              <span className="text-sm font-semibold text-gray-900">
                {AmountCalculator.formatGstPercent(calculationResult.gstPercent)}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Calculated as: GST Amount ÷ Base Amount × 100
            </p>
          </div>
        )}
      </div>

      {/* Final Amount Summary with Clean Design */}
      <div className="bg-info-50 border border-info-200 rounded-lg p-4">
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <TrendingUp className="h-5 w-5 text-info-600" />
            <h3 className="text-lg font-semibold text-gray-900">Total Expense Amount</h3>
          </div>

          <div className="space-y-1">
            <div className="text-3xl font-bold text-info-600">
              {AmountCalculator.formatAmount(calculationResult.total)}
            </div>
            <div className="text-sm text-gray-600">
              {enableGst && calculationResult.gstAmount > 0 ? (
                <span>Including GST/Tax: {AmountCalculator.formatAmount(calculationResult.gstAmount)}</span>
              ) : (
                <span>Tax not included</span>
              )}
            </div>
          </div>

          {/* Amount Breakdown */}
          {enableGst && calculationResult.gstAmount > 0 && (
            <div className="mt-3 p-3 bg-white rounded-lg border border-info-100">
              <div className="text-xs text-gray-600 space-y-1">
                <div className="flex justify-between">
                  <span>Base Amount:</span>
                  <span>{AmountCalculator.formatAmount(calculationResult.gross)}</span>
                </div>
                <div className="flex justify-between">
                  <span>GST/Tax ({AmountCalculator.formatGstPercent(calculationResult.gstPercent)}):</span>
                  <span>{AmountCalculator.formatAmount(calculationResult.gstAmount)}</span>
                </div>
                <hr className="border-gray-200" />
                <div className="flex justify-between font-medium text-gray-800">
                  <span>Total:</span>
                  <span>{AmountCalculator.formatAmount(calculationResult.total)}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Hidden fields for form values */}
        <input
          {...register("amount")}
          type="hidden"
          value={calculationResult.total}
        />
        <input
          {...register("gstPercent")}
          type="hidden"
          value={calculationResult.gstPercent}
        />
      </div>
    </div>
  );
};

export default UnifiedAmountModule;
